import { useRef, useState } from "react";
import { errorMessage } from "@/api";

export interface DraftStepDef {
  key: string;
  label: string;
  run: () => Promise<void>;
  /** Left out (e.g. no schedule to set). */
  skip?: boolean;
}

export type DraftStepStatus = "pending" | "running" | "done" | "failed";

export interface DraftStep {
  key: string;
  label: string;
  status: DraftStepStatus;
  error?: string;
}

/**
 * Runs a card's steps in order (create, then what the create endpoint can't take) and stops at the first
 * failure; `retry` resumes from the failed step, so nothing already created is created twice.
 */
export function useMultiStepCreate(defs: readonly DraftStepDef[], fallback?: string) {
  const active = defs.filter((d) => !d.skip);
  const [status, setStatus] = useState<Record<string, { status: DraftStepStatus; error?: string }>>({});
  const [busy, setBusy] = useState(false);
  const from = useRef(0);
  // `busy` disables the button only after a render; a second click can land before that.
  const running = useRef(false);

  const set = (key: string, next: { status: DraftStepStatus; error?: string }) =>
    setStatus((prev) => ({ ...prev, [key]: next }));

  const runFrom = async (start: number) => {
    if (running.current) return false;
    running.current = true;
    setBusy(true);
    try {
      for (let i = start; i < active.length; i++) {
        set(active[i].key, { status: "running" });
        try {
          await active[i].run();
        } catch (error) {
          set(active[i].key, { status: "failed", error: errorMessage(error, fallback) });
          from.current = i;
          return false;
        }
        set(active[i].key, { status: "done" });
      }
      from.current = active.length;
      return true;
    } finally {
      running.current = false;
      setBusy(false);
    }
  };

  const steps: DraftStep[] = active.map((d) => ({
    key: d.key,
    label: d.label,
    ...(status[d.key] ?? { status: "pending" }),
  }));
  return {
    steps,
    busy,
    error: steps.find((s) => s.status === "failed")?.error ?? null,
    done: steps.length > 0 && steps.every((s) => s.status === "done"),
    run: () => runFrom(0),
    retry: () => runFrom(from.current),
  };
}
